import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Trash2, Plus, Minus, Package } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';

const MiniCart = () => {
  const { 
    cartItems, 
    updateCartItem, 
    removeFromCart, 
    getCartTotal, 
    getCartItemCount 
  } = useCart();
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [updatingItem, setUpdatingItem] = useState(null);

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setUpdatingItem(itemId);
    try {
      await updateCartItem(itemId, newQuantity);
    } finally {
      setUpdatingItem(null);
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await removeFromCart(itemId);
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  if (!isAuthenticated) {
    return (
      <Link
        to="/login"
        className="relative p-2 text-gray-300 hover:text-white transition-colors"
        title="Se connecter"
      >
        <ShoppingCart className="h-6 w-6" />
      </Link>
    );
  }

  return (
    <div className="relative">
      {/* Cart Icon with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-300 hover:text-white transition-colors"
        title="Voir le panier"
      >
        <ShoppingCart className="h-6 w-6" />
        {getCartItemCount() > 0 && (
          <span className="absolute -top-1 -right-1 bg-iranian-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {getCartItemCount() > 99 ? '99+' : getCartItemCount()}
          </span>
        )}
      </button>

      {/* Mini Cart Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-full mt-2 w-80 bg-gray-800 rounded-xl shadow-2xl border border-gray-700 z-50"
          >
            <div className="p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">
                  Panier ({getCartItemCount()})
                </h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ×
                </button>
              </div>

              {/* Cart Items */}
              {cartItems.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-400">Votre panier est vide</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {cartItems.slice(0, 3).map((item) => (
                    <div key={item.id} className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg">
                      {/* Product Image */}
                      <img
                        src={item.primary_image || '/placeholder-product.svg'}
                        alt={item.name_fr}
                        className="w-12 h-12 object-cover rounded-lg"
                        onError={(e) => {
                          e.target.src = '/placeholder-product.svg';
                        }}
                      />

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white text-sm font-medium truncate">
                          {item.name_fr}
                        </h4>
                        <p className="text-iranian-400 text-sm font-semibold">
                          {formatPrice(item.sale_price || item.price)}
                        </p>
                      </div>

                      {/* Quantity Controls */}
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          disabled={updatingItem === item.id}
                          className="p-1 rounded-full bg-gray-600 hover:bg-gray-500 text-white disabled:opacity-50 text-xs"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        
                        <span className="text-white text-sm font-medium min-w-[1.5rem] text-center">
                          {updatingItem === item.id ? '...' : item.quantity}
                        </span>
                        
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          disabled={updatingItem === item.id}
                          className="p-1 rounded-full bg-gray-600 hover:bg-gray-500 text-white disabled:opacity-50 text-xs"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="p-1 text-red-400 hover:text-red-300 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}

                  {/* Show more indicator */}
                  {cartItems.length > 3 && (
                    <div className="text-center py-2">
                      <p className="text-gray-400 text-sm">
                        +{cartItems.length - 3} autre(s) article(s)
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Footer */}
              {cartItems.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-semibold">Total:</span>
                    <span className="text-iranian-400 font-semibold">
                      {formatPrice(getCartTotal())}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <Link
                      to="/cart"
                      onClick={() => setIsOpen(false)}
                      className="block w-full text-center py-2 px-4 bg-iranian-600 text-white rounded-lg hover:bg-iranian-700 transition-colors text-sm font-medium"
                    >
                      Voir le panier complet
                    </Link>
                    
                    <Link
                      to="/checkout"
                      onClick={() => setIsOpen(false)}
                      className="block w-full text-center py-2 px-4 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
                    >
                      Procéder au paiement
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default MiniCart;
